import React, { useState } from 'react';
import { getAuth } from 'firebase/auth';
import { useLabelAttributes } from '../../hooks/useLabelAttributes';
import {
  LabelAttribute,
  LabelAttributeOption,
} from '../../types/labelAttribute';

const NAME_PATTERN = /^[a-z0-9_]+$/;

type AttributeFormState = {
  name: string;
  displayName: string;
  description: string;
  guidanceNote: string;
  type: LabelAttribute['type'];
  level: LabelAttribute['level'];
  options: LabelAttributeOption[];
  defaultValue: any;
  required: boolean;
  active: boolean;
  appliesTo: string[];
};

const defaultFormState: AttributeFormState = {
  name: '',
  displayName: '',
  description: '',
  guidanceNote: '',
  type: 'enum',
  level: 'instance',
  options: [],
  defaultValue: null,
  required: false,
  active: true,
  appliesTo: [],
};

function attributeToFormState(attr: LabelAttribute): AttributeFormState {
  return {
    name: attr.name,
    displayName: attr.displayName,
    description: attr.description || '',
    guidanceNote: attr.guidanceNote || '',
    type: attr.type,
    level: attr.level,
    options: attr.options ? [...attr.options] : [],
    defaultValue: attr.defaultValue ?? null,
    required: attr.required,
    active: attr.active,
    appliesTo: attr.appliesTo ? [...attr.appliesTo] : [],
  };
}

export default function AttributeManager() {
  const {
    attributes,
    loading,
    error,
    createAttribute,
    updateAttribute,
    deleteAttribute,
  } = useLabelAttributes();

  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState<'all' | 'instance' | 'image'>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<LabelAttribute | null>(null);
  const [form, setForm] = useState<AttributeFormState>(defaultFormState);
  const [nameError, setNameError] = useState('');
  const [optionErrors, setOptionErrors] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);

  const filteredAttributes = attributes.filter((attr) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      attr.displayName.toLowerCase().includes(term) ||
      attr.name.toLowerCase().includes(term) ||
      (attr.description || '').toLowerCase().includes(term);
    const matchesLevel = levelFilter === 'all' || attr.level === levelFilter;
    return matchesSearch && matchesLevel;
  });

  const openCreateModal = () => {
    setEditingAttribute(null);
    setForm(defaultFormState);
    setNameError('');
    setOptionErrors({});
    setShowModal(true);
  };

  const openEditModal = (attr: LabelAttribute) => {
    setEditingAttribute(attr);
    setForm(attributeToFormState(attr));
    setNameError('');
    setOptionErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAttribute(null);
    setForm(defaultFormState);
    setNameError('');
    setOptionErrors({});
  };

  const validateName = (name: string) => {
    if (!name.trim()) {
      return 'Name is required';
    }
    if (!NAME_PATTERN.test(name)) {
      return 'Name must contain only lowercase letters, numbers, and underscores';
    }
    return '';
  };

  const validateOptions = (options: LabelAttributeOption[]) => {
    const errors: Record<number, string> = {};
    options.forEach((opt, index) => {
      if (!opt.value.trim()) {
        errors[index] = 'Value is required';
      } else if (!NAME_PATTERN.test(opt.value)) {
        errors[index] = 'Value must contain only lowercase letters, numbers, and underscores';
      }
    });
    return errors;
  };

  const handleFormChange = (
    field: keyof AttributeFormState,
    value: string | boolean | LabelAttributeOption[] | any
  ) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === 'type') {
        const newType = value as LabelAttribute['type'];
        if (newType !== 'enum' && newType !== 'multi_enum') {
          next.options = [];
        }
        if (newType === 'boolean') {
          next.defaultValue = false;
        } else if (newType === 'multi_enum') {
          next.defaultValue = [];
        } else {
          next.defaultValue = null;
        }
      }

      return next;
    });

    if (field === 'name' && typeof value === 'string' && !editingAttribute) {
      setNameError(validateName(value));
    }
  };

  const addOptionRow = () => {
    setForm((prev) => ({
      ...prev,
      options: [...prev.options, { value: '', label: '', description: '' }],
    }));
  };

  const removeOptionRow = (index: number) => {
    setForm((prev) => {
      const removedValue = prev.options[index]?.value;
      const nextOptions = prev.options.filter((_, i) => i !== index);
      let nextDefault = prev.defaultValue;

      if (prev.type === 'enum' && prev.defaultValue === removedValue) {
        nextDefault = null;
      }
      if (prev.type === 'multi_enum' && Array.isArray(prev.defaultValue)) {
        nextDefault = prev.defaultValue.filter((v: string) => v !== removedValue);
      }

      return { ...prev, options: nextOptions, defaultValue: nextDefault };
    });
    setOptionErrors((prev) => {
      const next: Record<number, string> = {};
      Object.entries(prev).forEach(([key, msg]) => {
        const idx = Number(key);
        if (idx < index) next[idx] = msg;
        else if (idx > index) next[idx - 1] = msg;
      });
      return next;
    });
  };

  const updateOption = (index: number, field: keyof LabelAttributeOption, value: string) => {
    setForm((prev) => {
      const nextOptions = prev.options.map((opt, i) =>
        i === index ? { ...opt, [field]: value } : opt
      );
      let nextDefault = prev.defaultValue;

      if (field === 'value' && prev.type === 'enum' && prev.defaultValue === prev.options[index]?.value) {
        nextDefault = value || null;
      }
      if (field === 'value' && prev.type === 'multi_enum' && Array.isArray(prev.defaultValue)) {
        const oldValue = prev.options[index]?.value;
        nextDefault = prev.defaultValue.map((v: string) => (v === oldValue ? value : v));
      }

      return { ...prev, options: nextOptions, defaultValue: nextDefault };
    });

    if (field === 'value') {
      setOptionErrors((prev) => {
        const next = { ...prev };
        const err = !value.trim()
          ? 'Value is required'
          : !NAME_PATTERN.test(value)
            ? 'Value must contain only lowercase letters, numbers, and underscores'
            : '';
        if (err) next[index] = err;
        else delete next[index];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const nameErr = editingAttribute ? '' : validateName(form.name);
    setNameError(nameErr);

    const optErrors =
      form.type === 'enum' || form.type === 'multi_enum'
        ? validateOptions(form.options)
        : {};
    setOptionErrors(optErrors);

    if (nameErr || Object.keys(optErrors).length > 0) {
      return;
    }

    if ((form.type === 'enum' || form.type === 'multi_enum') && form.options.length === 0) {
      alert('Please add at least one option for enum types.');
      return;
    }

    const auth = getAuth();
    const uid = auth.currentUser?.uid || 'unknown';

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        displayName: form.displayName.trim(),
        description: form.description.trim(),
        guidanceNote: form.guidanceNote.trim(),
        type: form.type,
        level: form.level,
        options: form.type === 'enum' || form.type === 'multi_enum' ? form.options : [],
        defaultValue: form.defaultValue,
        required: form.required,
        appliesTo: editingAttribute ? form.appliesTo : [],
        active: form.active,
      };

      if (editingAttribute) {
        await updateAttribute(editingAttribute.id, {
          ...payload,
          version: (editingAttribute.version || 1) + 1,
        });
      } else {
        await createAttribute({
          ...payload,
          version: 1,
          createdBy: uid,
        });
      }

      closeModal();
    } catch (err) {
      console.error('Error saving attribute:', err);
      alert('Failed to save attribute. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (attr: LabelAttribute) => {
    const confirmed = window.confirm(
      `Delete attribute "${attr.displayName}" (${attr.name})?\n\nAny schema referencing this attribute will lose it. This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await deleteAttribute(attr.id);
    } catch (err) {
      console.error('Error deleting attribute:', err);
      alert('Failed to delete attribute. Please try again.');
    }
  };

  const showOptionsEditor = form.type === 'enum' || form.type === 'multi_enum';

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <p className="text-gray-600">Loading attributes...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Label Attributes</h1>
          <p className="text-gray-600 mt-2">
            Manage reusable annotation attributes referenced by detection schemas
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          + New Attribute
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Search by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        <select
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value as 'all' | 'instance' | 'image')}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All levels</option>
          <option value="instance">Instance</option>
          <option value="image">Image</option>
        </select>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Display Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Options</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAttributes.map((attr) => (
              <tr key={attr.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">{attr.displayName}</td>
                <td className="px-4 py-3 text-sm font-mono text-gray-600">{attr.name}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{attr.type}</td>
                <td className="px-4 py-3 text-sm text-gray-700 capitalize">{attr.level}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{attr.options?.length ?? 0}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{attr.required ? 'Yes' : 'No'}</td>
                <td className="px-4 py-3 text-sm">
                  <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                    attr.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {attr.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-right space-x-2">
                  <button
                    onClick={() => openEditModal(attr)}
                    className="px-3 py-1 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(attr)}
                    className="px-3 py-1 text-red-600 hover:text-red-800 font-medium"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredAttributes.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No attributes found.
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingAttribute ? 'Edit Attribute' : 'New Attribute'}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  &times;
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Machine name *
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      readOnly={!!editingAttribute}
                      required
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        editingAttribute ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'border-gray-300'
                      } ${nameError ? 'border-red-500' : ''}`}
                      placeholder="e.g. ripeness"
                    />
                    {editingAttribute ? (
                      <p className="text-xs text-gray-500 mt-1">
                        Machine name is permanent once annotations exist.
                      </p>
                    ) : nameError ? (
                      <p className="text-xs text-red-600 mt-1">{nameError}</p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">
                        Lowercase letters, numbers, and underscores only
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display name *
                    </label>
                    <input
                      type="text"
                      value={form.displayName}
                      onChange={(e) => handleFormChange('displayName', e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. Ripeness"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="What this attribute means"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Guidance note</label>
                  <textarea
                    value={form.guidanceNote}
                    onChange={(e) => handleFormChange('guidanceNote', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Annotator guidance (optional)"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                    <select
                      value={form.type}
                      onChange={(e) => handleFormChange('type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="enum">enum</option>
                      <option value="multi_enum">multi_enum</option>
                      <option value="boolean">boolean</option>
                      <option value="number">number</option>
                      <option value="text">text</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Level *</label>
                    <select
                      value={form.level}
                      onChange={(e) => handleFormChange('level', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="instance">instance (per bounding box)</option>
                      <option value="image">image (whole photo)</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.required}
                      onChange={(e) => handleFormChange('required', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Required</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => handleFormChange('active', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>

                {showOptionsEditor && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">Options</label>
                      <button
                        type="button"
                        onClick={addOptionRow}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        + Add option
                      </button>
                    </div>
                    <div className="space-y-3">
                      {form.options.map((opt, index) => (
                        <div key={index} className="grid grid-cols-12 gap-2 items-start border border-gray-200 rounded-lg p-3">
                          <div className="col-span-3">
                            <input
                              type="text"
                              value={opt.value}
                              onChange={(e) => updateOption(index, 'value', e.target.value)}
                              placeholder="value"
                              className={`w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono ${
                                optionErrors[index] ? 'border-red-500' : 'border-gray-300'
                              }`}
                            />
                            {optionErrors[index] && (
                              <p className="text-xs text-red-600 mt-0.5">{optionErrors[index]}</p>
                            )}
                          </div>
                          <div className="col-span-3">
                            <input
                              type="text"
                              value={opt.label}
                              onChange={(e) => updateOption(index, 'label', e.target.value)}
                              placeholder="label"
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          <div className="col-span-5">
                            <input
                              type="text"
                              value={opt.description}
                              onChange={(e) => updateOption(index, 'description', e.target.value)}
                              placeholder="description"
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          <div className="col-span-1 flex justify-end">
                            <button
                              type="button"
                              onClick={() => removeOptionRow(index)}
                              className="text-red-500 hover:text-red-700 text-lg leading-none"
                              title="Remove option"
                            >
                              &times;
                            </button>
                          </div>
                        </div>
                      ))}
                      {form.options.length === 0 && (
                        <p className="text-sm text-gray-500 italic">No options yet. Add at least one.</p>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default value</label>
                  {form.type === 'enum' && (
                    <select
                      value={form.defaultValue ?? ''}
                      onChange={(e) => handleFormChange('defaultValue', e.target.value || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">None</option>
                      {form.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label || opt.value}
                        </option>
                      ))}
                    </select>
                  )}
                  {form.type === 'multi_enum' && (
                    <div className="flex flex-wrap gap-2 border border-gray-200 rounded-lg p-3">
                      {form.options.length === 0 ? (
                        <p className="text-sm text-gray-500">Add options first</p>
                      ) : (
                        form.options.map((opt) => {
                          const selected = Array.isArray(form.defaultValue) && form.defaultValue.includes(opt.value);
                          return (
                            <label
                              key={opt.value}
                              className="inline-flex items-center gap-2 border border-gray-200 rounded px-2 py-1 text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={(e) => {
                                  const cur = Array.isArray(form.defaultValue) ? [...form.defaultValue] : [];
                                  const next = e.target.checked
                                    ? [...cur, opt.value]
                                    : cur.filter((v) => v !== opt.value);
                                  handleFormChange('defaultValue', next);
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span>{opt.label || opt.value}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  )}
                  {form.type === 'boolean' && (
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!form.defaultValue}
                        onChange={(e) => handleFormChange('defaultValue', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Default to true</span>
                    </label>
                  )}
                  {form.type === 'number' && (
                    <input
                      type="number"
                      value={form.defaultValue ?? ''}
                      onChange={(e) =>
                        handleFormChange(
                          'defaultValue',
                          e.target.value === '' ? null : Number(e.target.value)
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                  {form.type === 'text' && (
                    <input
                      type="text"
                      value={form.defaultValue ?? ''}
                      onChange={(e) => handleFormChange('defaultValue', e.target.value || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : editingAttribute ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
